import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { logout, refreshToken } from "redux/slices/authSlice";
import { tokenStorage } from "utils/tokenStorage";
import env from "env";

type BaseQuery = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;
type BaseQueryResult = ReturnType<BaseQuery>;

const handleClear = async (dispatch: any) => {
  const isAutoNavigateWso2Auth =
    env.app.authResourceOwner === "false" && env.app.authCodeEnabled === "true";

  await dispatch(logout());

  setTimeout(() => {
    if (isAutoNavigateWso2Auth) {
      window.location.href = `${env.api.baseUrl.tokenBaseUrl}oidc/logout?client_id=${env.app.authCodeAppId}&post_logout_redirect_uri=${env.app.rootUrl}`;
    } else {
      window.location.href = "/";
    }
  }, 300);
};

// Request queue for managing concurrent requests during token refresh
let isRefreshing = false;
let failedQueue: Array<{ resolve: Function; reject: Function }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

const handleTokenRefresh = async (dispatch: any, baseQuery: BaseQuery, args: any, api: any, extraOptions: any): Promise<Awaited<BaseQueryResult> | null> => {
  const { getState } = api;
  const refreshTokenValue = getState().auth.refreshToken;
  
  if (!refreshTokenValue) {
    console.log("🔐 [BASE QUERY] No refresh token available");
    handleClear(dispatch);
    return null;
  }

  try {
    console.log("🔐 [BASE QUERY] Starting transparent token refresh");
    const resp = await dispatch(refreshToken()).unwrap();

    if (resp && resp.access_token && resp.refresh_token) {
      console.log("🔐 [BASE QUERY] Transparent refresh successful, retrying request");
      processQueue(null, resp.access_token);
      return await baseQuery(args, api, extraOptions);
    } else {
      console.error("🔐 [BASE QUERY] Invalid refresh response");
      processQueue(new Error("Invalid refresh response"), null);
      handleClear(dispatch);
      return null;
    }
  } catch (error) {
    console.error("🔐 [BASE QUERY] Token refresh failed", error);
    processQueue(error, null);
    handleClear(dispatch);
    return null;
  }
};

const handleQueuedRequest = (baseQuery: BaseQuery, args: any, api: any, extraOptions: any): Promise<Awaited<BaseQueryResult>> => {
  return new Promise((resolve, reject) => {
    failedQueue.push({
      resolve: async (token: string) => {
        try {
          console.log("🔐 [BASE QUERY] Retrying queued request with new token");
          const retryResult = await baseQuery(args, api, extraOptions);
          resolve(retryResult);
        } catch (err) {
          reject(err);
        }
      },
      reject,
    });
  });
};

export const baseQueryCreator = (baseQuery: BaseQuery) => {
  const baseQueryWithReauth: BaseQuery = async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);
    const { dispatch }: any = api;

    // Handle 403 Forbidden errors (organization mismatch) - don't retry
    if (result.error?.status === 403) {
      return result;
    }

    // Handle 401 Unauthorized errors - attempt token refresh
    if (result.error?.status !== 401) {
      return result;
    }

    console.log("🔐 [BASE QUERY] 401 detected, attempting transparent token refresh");
    
    // If already refreshing, queue this request
    if (isRefreshing) {
      console.log("🔐 [BASE QUERY] Already refreshing, queueing request");
      return handleQueuedRequest(baseQuery, args, api, extraOptions);
    }

    // Start token refresh process
    isRefreshing = true;
    
    try {
      const refreshResult = await handleTokenRefresh(dispatch, baseQuery, args, api, extraOptions);
      return refreshResult || result;
    } finally {
      isRefreshing = false;
    }
  };

  return baseQueryWithReauth;
};

export const baseQueryWithAppToken = (baseQuery: BaseQuery) => {
  const baseQueryCustom: BaseQuery = async (args: any, api, extraOptions) => {
    const response = await fetch(
      `${env.api.baseUrl.tokenBaseUrl}oauth2/token`,
      {
        method: "POST",
        headers: {
          Authorization: `Basic ${btoa(
            `${env.api.clientId}:${env.api.clientSecret}`
          )}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: (function () {
          const formBody = [{ key: "grant_type", value: "client_credentials" }]
            .map(
              (n) =>
                encodeURIComponent(n.key) + "=" + encodeURIComponent(n.value)
            )
            .join("&");
          return formBody;
        })(),
      }
    );
    const content = await response.json();
    args.headers = {
      ...args.headers,
      Authorization: `Bearer ${content?.access_token}`,
    };
    let result: any = await baseQuery(args, api, extraOptions);
    if (result?.error?.data) {
      return {
        error: result?.error,
      };
    }
    return result;
  };

  return baseQueryCustom;
};

// Helper function for transparent token headers (DEPRECATED - use Redux state instead)
export const prepareTransparentHeaders = (headers: Headers) => {
  console.warn("🔧 [HEADERS] prepareTransparentHeaders is deprecated - use Redux state instead");
  // For backward compatibility, try to get token from Redux store if available
  // This is a fallback - all services should use getState().auth.accessToken directly
  const token = tokenStorage.getAccessToken();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
    console.log("🔧 [HEADERS] Set authorization header from transparent storage (deprecated)");
  } else {
    console.log("🔧 [HEADERS] No access token available in transparent storage");
  }
  return headers;
};

// https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#automatic-re-authorization-by-extending-fetchbasequery
